import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'stv_homepage_popup_last_shown';
const SESSION_KEY = 'stv_homepage_popup_shown_session';

const HomepagePopup = ({ config }) => {
  const [open, setOpen] = useState(false);

  const canShow = useCallback(() => {
    if (!config?.enabled) return false;
    if (config.frequency === 'once_per_session' && window.sessionStorage.getItem(SESSION_KEY) === 'true') return false;
    if (config.frequency === 'once_per_day') {
      const lastShown = window.localStorage.getItem(STORAGE_KEY);
      if (lastShown && Date.now() - Number(lastShown) < 24 * 60 * 60 * 1000) return false;
    }
    return true;
  }, [config]);

  const showPopup = useCallback(() => {
    if (!canShow()) return;
    setOpen(true);
    window.sessionStorage.setItem(SESSION_KEY, 'true');
    window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
  }, [canShow]);

  const closePopup = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!config?.enabled) {
      setOpen(false);
      return undefined;
    }

    const triggerMode = config.trigger_mode || 'entry';
    if (triggerMode === 'exit_intent') {
      const handleMouseOut = (event) => {
        if (event.clientY <= 0 && !event.relatedTarget) showPopup();
      };
      document.addEventListener('mouseout', handleMouseOut);
      return () => document.removeEventListener('mouseout', handleMouseOut);
    }

    const delay = triggerMode === 'delay' ? Math.max(Number(config.delay_seconds || 0), 0) * 1000 : 0;
    const timer = window.setTimeout(showPopup, delay);
    return () => window.clearTimeout(timer);
  }, [config, showPopup]);

  useEffect(() => {
    if (!open || config?.close_on_escape === false) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closePopup();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, config?.close_on_escape, closePopup]);

  if (!config?.enabled || !open) return null;

  const headline = config.headline || 'Secure the Vote Maryland';
  const subheadline = config.subheadline || '';
  const body = config.body || '';
  const buttonText = config.button_text || 'Learn More';
  const rawButtonLink = (config.button_link || '/').trim();
  const isInternalLink = rawButtonLink.startsWith('/') || rawButtonLink.startsWith('#');
  const isSpecialLink = /^(mailto:|tel:)/i.test(rawButtonLink);
  const isExternalLink = !isInternalLink && (isSpecialLink || /^[a-z][a-z0-9+.-]*:/i.test(rawButtonLink) || rawButtonLink.includes('.'));
  const buttonLink = isExternalLink && !isSpecialLink && !/^[a-z][a-z0-9+.-]*:/i.test(rawButtonLink)
    ? `https://${rawButtonLink}`
    : rawButtonLink;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-950/65 px-4 py-6 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="homepage-popup-title"
      onMouseDown={(event) => {
        if (config.close_on_overlay !== false && event.target === event.currentTarget) closePopup();
      }}
    >
      <div className="relative w-full max-w-2xl max-h-[calc(100dvh-3rem)] overflow-y-auto rounded-2xl bg-white shadow-2xl border border-[#D8B45F]/40">
        <div className="absolute inset-x-0 top-0 h-2 bg-gradient-to-r from-[#9B1E37] via-[#D8B45F] to-[#1f3b73]" />
        <button
          type="button"
          onClick={closePopup}
          className="absolute right-4 top-4 rounded-full bg-white/90 p-2 text-slate-500 shadow-sm transition hover:text-[#9B1E37] focus:outline-none focus:ring-2 focus:ring-[#9B1E37]"
          aria-label="Close popup"
        >
          <X size={20} />
        </button>

        <div className="bg-[radial-gradient(circle_at_top_left,_rgba(155,30,55,0.12),_transparent_35%),linear-gradient(135deg,_#fff,_#f8f4ed)] px-8 py-10 sm:px-12">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.24em] text-[#9B1E37]">Secure the Vote Maryland</p>
          <h2 id="homepage-popup-title" className="text-3xl font-extrabold leading-tight text-slate-950 sm:text-4xl">
            {headline}
          </h2>
          {subheadline && (
            <p className="mt-3 text-lg font-semibold text-[#1f3b73]">{subheadline}</p>
          )}
          {body && (
            <div className="mt-5 whitespace-pre-line text-base leading-7 text-slate-700">{body}</div>
          )}
          {buttonLink && (
            <Button asChild className="mt-7 rounded-full px-7 py-6 text-base font-bold shadow-lg" style={{ backgroundColor: '#9B1E37' }}>
              <a
                href={buttonLink}
                target={isExternalLink ? '_blank' : '_self'}
                rel={isExternalLink ? 'noopener noreferrer' : undefined}
                onClick={(event) => {
                  if (!isExternalLink) return;
                  event.preventDefault();
                  window.open(buttonLink, '_blank', 'noopener,noreferrer');
                }}
              >
                {buttonText}
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomepagePopup;
